import type * as React from "react";
import { useParams } from "react-router-dom";
import ErrorBoundary from "~/components/ErrorBoundary";
import { ReloadableRouteConfiguration, useReloadableRouteIgnoreKeys } from "~/components/ReloadableRoute/ReloadableRouteConfiguration";
export interface ReloadableRouteContentProps {
    doNotReloadWhenTheseKeysChange?: string[];
    children: React.ReactNode;
}
const ReloadableRouteContent: React.FC<ReloadableRouteContentProps> = ({ doNotReloadWhenTheseKeysChange, children }) => {
    // We are tagging the parent route of each component with a key made out of params it depends
    // on so redirects to the same component with different params unmount it and mount it again.
    // This can also be done by responding to changes in props but this is a lot of work to do it
    // correctly and it is very easy to forget about it.
    // Details: https://github.com/ReactTraining/react-router/issues/1982
    const ignoreKeysFromContext = useReloadableRouteIgnoreKeys();
    const ignoreKeys = doNotReloadWhenTheseKeysChange ?? ignoreKeysFromContext ?? [];
    const { "*": splat, ...params } = useParams<Record<string, string>>(); // We need to ignore the splat since this will change for all page navigations
    const key = getComponentKey(params, ignoreKeys);
    return <ErrorBoundary key={key}>{doNotReloadWhenTheseKeysChange === undefined ? children : <ReloadableRouteConfiguration doNotReloadWhenTheseKeysChange={doNotReloadWhenTheseKeysChange}>{children}</ReloadableRouteConfiguration>}</ErrorBoundary>;
};
ReloadableRouteContent.displayName = "ReloadableRouteContent"
export function getComponentKey(params: Record<string, string | undefined>, ignoreTheseKeys: string[]) {
    if (Object.keys(params).length === 0)
        return "";
    const keyValues = Object.keys(params)
        .filter((key) => !ignoreTheseKeys.includes(key))
        .map((key) => params[key]);
    return keyValues.filter((x) => x).join("/");
}
export default ReloadableRouteContent;
